import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Lock, Unlock, CheckCircle, XCircle, Loader2, ChevronRight, Clock, BookOpen, Play, Menu, X, Trophy, Eye, Youtube } from 'lucide-react'
import { guestPlayerService } from '@/services/guestPlayerService'
import { BlocklyActivity } from '@/components/blockly/BlocklyActivity'
import { PotatoPiratesActivity } from '@/components/potatoPirates/PotatoPiratesActivity'
import { ShellActivity } from '@/components/player/ShellActivity'
import { PhishingQuizActivity } from '@/components/player/PhishingQuizActivity'
import { PacketAnalysisActivity } from '@/components/player/PacketAnalysisActivity'
import { CyberHygieneQuizActivity } from '@/components/player/CyberHygieneQuizActivity'
import { CodingQuizActivity } from '@/components/player/CodingQuizActivity'
import { PythonReplActivity } from '@/components/player/PythonReplActivity'
import { McqActivity } from '@/components/player/McqActivity'
import { BreachCheckActivity } from '@/components/player/BreachCheckActivity'
import { PasswordCheckActivity } from '@/components/player/PasswordCheckActivity'
import type { SelfPacedActivity, SelfPacedProgress } from '@/types'
import styles from './SelfPacedPlayerView.module.css'

interface SelfPacedPlayerViewProps {
  runCode: string
  lessonName: string
  playerName: string
  sessionValid: boolean
}

export function SelfPacedPlayerView({ runCode, lessonName, playerName, sessionValid }: SelfPacedPlayerViewProps) {
  const [activities, setActivities] = useState<SelfPacedActivity[]>([])
  const [progress, setProgress] = useState<SelfPacedProgress | null>(null)
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [responseData, setResponseData] = useState<Record<string, any>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [timedOut, setTimedOut] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const [currentSlide, setCurrentSlide] = useState(1)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const selectedActivity = activities.find(a => a.id === selectedActivityId)

  const loadProgress = useCallback(async () => {
    const result = await guestPlayerService.getSelfPacedProgress(runCode)
    if (result.success && result.data) {
      setActivities(result.data.activities || [])
      setProgress(result.data.progress_summary || null)

      if (!selectedActivityId) {
        const current = (result.data.activities || []).find(
          (a: SelfPacedActivity) => a.progress_status === 'in_progress' || a.progress_status === 'unlocked'
        )
        if (current) setSelectedActivityId(current.id)
        else if (result.data.activities?.length > 0) setSelectedActivityId(result.data.activities[0].id)
      }
    } else {
      setError(result.error || 'Failed to load progress')
    }
    setLoading(false)
  }, [runCode, selectedActivityId])

  useEffect(() => {
    loadProgress()
  }, [loadProgress])

  // Listen for host time extensions via WebSocket
  useEffect(() => {
    const handleTimeUpdated = (data: any) => {
      const activityId = data.id
      const newLimit = data.time_limit_seconds
      if (activityId && typeof newLimit === 'number') {
        setActivities(prev => prev.map(a =>
          a.id === activityId ? { ...a, time_limit_seconds: newLimit } : a
        ))
      }
    }
    guestPlayerService.on('activity_time_updated', handleTimeUpdated)
    return () => { guestPlayerService.off('activity_time_updated', handleTimeUpdated) }
  }, [])

  // Reset slide counter when switching activities
  useEffect(() => {
    setCurrentSlide(1)
  }, [selectedActivityId])

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    setTimedOut(false)

    if (selectedActivity?.progress_status === 'in_progress' && selectedActivity.started_at && selectedActivity.time_limit_seconds) {
      const startedAt = new Date(selectedActivity.started_at).getTime()
      const limit = selectedActivity.time_limit_seconds * 1000

      // Check if already expired on load (e.g. page refresh after time ran out)
      const initialRemaining = Math.max(0, Math.floor((startedAt + limit - Date.now()) / 1000))
      if (initialRemaining === 0) {
        setTimeRemaining(0)
        setTimedOut(true)
        completeAndAdvance(selectedActivity.id)
        return
      }

      setTimeRemaining(initialRemaining)
      timerRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.floor((startedAt + limit - Date.now()) / 1000))
        setTimeRemaining(remaining)
        if (remaining === 0) {
          if (timerRef.current) clearInterval(timerRef.current)
          setTimedOut(true)
          completeAndAdvance(selectedActivity.id)
        }
      }, 1000)
    } else {
      setTimeRemaining(null)
    }

    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [selectedActivity?.id, selectedActivity?.progress_status, selectedActivity?.started_at, selectedActivity?.time_limit_seconds])

  const handleStartActivity = async (activityId: string) => {
    setActionLoading(true)
    const result = await guestPlayerService.startSelfPacedActivity(runCode, activityId)
    if (result.success) {
      await loadProgress()
    } else {
      setError(result.error || 'Failed to start activity')
    }
    setActionLoading(false)
  }

  /** Submit response only — does NOT complete or navigate (lets the component show explanation first) */
  const submitOnly = async (activityId: string, data: Record<string, any>): Promise<{ success: boolean; error?: string }> => {
    const result = await guestPlayerService.submitResponse(runCode, activityId, data)
    return { success: result.success, error: result.error }
  }

  /** Complete activity and advance to next — auto-starts next activity to skip the unlocked screen */
  const completeAndAdvance = async (activityId: string) => {
    const completeResult = await guestPlayerService.completeSelfPacedActivity(runCode, activityId)
    if (completeResult.success && completeResult.data?.next_activity) {
      const nextId = completeResult.data.next_activity.id
      await guestPlayerService.startSelfPacedActivity(runCode, nextId)
      setSelectedActivityId(nextId)
    }
    await loadProgress()
  }

  /** Submit + complete in one step (for activity types without an explanation pause, e.g. shell, blockly) */
  const submitAndComplete = async (activityId: string, data: Record<string, any>): Promise<{ success: boolean; error?: string }> => {
    const result = await guestPlayerService.submitResponse(runCode, activityId, data)
    if (result.success) {
      await completeAndAdvance(activityId)
    }
    return { success: result.success, error: result.error }
  }

  const handleCompleteActivity = async (activityId: string) => {
    setActionLoading(true)
    const result = await guestPlayerService.completeSelfPacedActivity(runCode, activityId)
    if (result.success) {
      setResponseData({})
      if (result.data?.next_activity) {
        const nextId = result.data.next_activity.id
        await guestPlayerService.startSelfPacedActivity(runCode, nextId)
        setSelectedActivityId(nextId)
      }
      await loadProgress()
    } else {
      setError(result.error || 'Failed to complete activity')
    }
    setActionLoading(false)
  }

  const handleSubmitResponse = async () => {
    if (!selectedActivity) return
    setIsSubmitting(true)
    setError(null)

    const result = await guestPlayerService.submitResponse(runCode, selectedActivity.id, responseData)

    if (result.success) {
      setResponseData({})
      await loadProgress()
      if (result.data?.next_activity) {
        setSelectedActivityId(result.data.next_activity.id)
      }
    } else {
      setError(result.error || 'Failed to submit response')
    }
    setIsSubmitting(false)
  }

  const handleResponseChange = (field: string, value: any) => {
    setResponseData(prev => ({ ...prev, [field]: value }))
  }

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const renderActivityContent = (activity: SelfPacedActivity) => {
    // Locked
    if (activity.progress_status === 'locked') {
      return (
        <div className={styles.stateCenter}>
          <Lock className={`${styles.stateIconWrap} ${styles.stateIconLock}`} />
          <h3 className={styles.stateHeading}>Activity Locked</h3>
          <p className={styles.stateDesc}>Complete the previous activity to unlock this one.</p>
        </div>
      )
    }

    // Unlocked — needs start (only show if not mid-transition)
    if (activity.progress_status === 'unlocked' && !actionLoading) {
      return (
        <div className={styles.stateCenter}>
          <BookOpen className={`${styles.stateIconWrap} ${styles.stateIconReady}`} />
          <h3 className={styles.stateHeading}>{activity.title}</h3>
          {activity.description && <p className={styles.stateDesc}>{activity.description}</p>}
          <p className={styles.stateTypeBadge}>
            {({'mcq': 'Multiple Choice', 'python_repl': 'Python Coding', 'coding': 'Blockly Coding'}[activity.activity_type] || activity.activity_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}
            {activity.time_limit_seconds && ` — ${activity.time_limit_seconds >= 60 ? `${Math.floor(activity.time_limit_seconds / 60)} min` : `${activity.time_limit_seconds} sec`} time limit`}
          </p>
          <button
            onClick={() => handleStartActivity(activity.id)}
            className={styles.btnStart}
          >
            <Play className="w-4 h-4" />
            Start Activity
          </button>
        </div>
      )
    }


    // In progress — render by type
    const actType = activity.activity_type || activity.type

    switch (actType) {
      case 'google_slides':
        return (
          <div className={styles.stack4}>
            <h3 className={styles.heading}>{activity.title}</h3>
            {activity.description && <p className={styles.description}>{activity.description}</p>}
            {activity.activity_data?.embed_url ? (
              <div className={styles.embedWrap}>
                <div className="absolute inset-0 flex items-center justify-center bg-surface-deep rounded-lg z-0">
                  <Loader2 className="w-8 h-8 animate-spin text-theme-text-muted" />
                </div>
                <iframe
                  src={activity.activity_data.embed_url}
                  className={`${styles.embedFrame} z-[1]`}
                  allowFullScreen
                  sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
                />
              </div>
            ) : (
              <p className={styles.stateDesc}>No slides available.</p>
            )}
            {!activity.player_has_responded && (
              <button
                onClick={() => handleCompleteActivity(activity.id)}
                disabled={actionLoading}
                className={styles.btnComplete}
              >
                {actionLoading ? 'Completing...' : 'Mark as Viewed'}
              </button>
            )}
          </div>
        )

      case 'youtube_video':
        return (
          <div className={styles.stack4}>
            <h3 className={styles.heading}>{activity.title}</h3>
            {activity.description && <p className={styles.description}>{activity.description}</p>}
            {activity.activity_data?.embed_url ? (
              <div className={styles.embedWrap}>
                <iframe
                  src={activity.activity_data.embed_url}
                  className={styles.embedFrame}
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title="YouTube Video"
                />
              </div>
            ) : (
              <p className={styles.stateDesc}>No video available.</p>
            )}
            {!activity.player_has_responded && (
              <button
                onClick={() => handleCompleteActivity(activity.id)}
                disabled={actionLoading}
                className={styles.btnComplete}
              >
                {actionLoading ? 'Completing...' : 'Mark as Viewed'}
              </button>
            )}
          </div>
        )

      case 'pdf_slideshow': {
        const totalSlides = activity.activity_data?.total_slides || 0
        return (
          <div className={styles.stack4}>
            <h3 className={styles.heading}>{activity.title}</h3>
            {activity.description && <p className={styles.description}>{activity.description}</p>}
            {totalSlides > 0 ? (
              <div>
                <div className={styles.slideContainer}>
                  <img
                    src={`/api/v1/lessons/${activity.lesson_id}/activities/${activity.id}/slides?page=${currentSlide}`}
                    alt={`Slide ${currentSlide}`}
                    className="w-full h-auto"
                  />
                </div>
                <div className={styles.slideNav}>
                  <button
                    onClick={() => setCurrentSlide(s => Math.max(1, s - 1))}
                    disabled={currentSlide <= 1}
                    className={styles.slideBtn}
                  >
                    Previous
                  </button>
                  <span className={styles.slideCounter}>
                    {currentSlide} / {totalSlides}
                  </span>
                  <button
                    onClick={() => setCurrentSlide(s => Math.min(totalSlides, s + 1))}
                    disabled={currentSlide >= totalSlides}
                    className={styles.slideBtn}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <p className={styles.stateDesc}>No slides available.</p>
            )}
            <button
              onClick={() => handleCompleteActivity(activity.id)}
              disabled={actionLoading}
              className={styles.btnComplete}
            >
              {actionLoading ? 'Completing...' : 'Mark as Viewed'}
            </button>
          </div>
        )
      }

      case 'mcq':
        return (
          <McqActivity
            activityData={activity.activity_data}
            description={activity.description || undefined}
            disabled={activity.player_has_responded || timedOut}
            onSubmitResponse={async (data) => submitOnly(activity.id, data)}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      case 'command_shell':
        return (
          <ShellActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded || timedOut}
            onExecuteCommand={async (cmd) => {
              return await guestPlayerService.executeShellCommand(runCode, activity.id, cmd)
            }}
            onSubmitAnswer={async (answer) => submitAndComplete(activity.id, { answer })}
          />
        )

      case 'phishing_quiz':
        return (
          <PhishingQuizActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded || timedOut}
            onSubmitQuiz={async (data) => submitOnly(activity.id, data)}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      case 'packet_analysis':
        return (
          <PacketAnalysisActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded || timedOut}
            onSubmitQuiz={async (data) => submitOnly(activity.id, data)}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      case 'cyber_hygiene_quiz':
        return (
          <CyberHygieneQuizActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded || timedOut}
            onSubmitQuiz={async (data) => submitOnly(activity.id, data)}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      case 'coding_quiz':
        return (
          <CodingQuizActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded || timedOut}
            onSubmitQuiz={async (data) => submitOnly(activity.id, data)}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      case 'python_repl':
        return (
          <PythonReplActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded || timedOut}
            onSubmitResponse={async (data) => submitOnly(activity.id, data)}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      case 'breach_check':
        return (
          <BreachCheckActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded || timedOut}
            runCode={runCode}
            onSubmitResponse={async (data) => submitOnly(activity.id, data)}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      case 'password_check':
        return (
          <PasswordCheckActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded || timedOut}
            runCode={runCode}
            onSubmitResponse={async (data) => submitOnly(activity.id, data)}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      case 'coding':
        return (
          <BlocklyActivity
            activity={{
              id: activity.id,
              type: activity.activity_type,
              title: activity.title,
              description: activity.description || '',
              status: 'active',
              time_limit: activity.time_limit_seconds || 0,
              started_at: activity.started_at || null,
              ends_at: null,
              allows_multiple_responses: activity.allows_multiple_responses,
              player_has_responded: activity.player_has_responded,
              activity_data: activity.activity_data
            }}
            disabled={activity.player_has_responded || timedOut}
            onSubmit={async (data) => {
              setIsSubmitting(true)
              await guestPlayerService.submitResponse(runCode, activity.id, data)
              setIsSubmitting(false)
            }}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      case 'potato_pirates':
        return (
          <PotatoPiratesActivity
            key={activity.id}
            activity={{
              id: activity.id,
              title: activity.title,
              activity_data: activity.activity_data,
            }}
            disabled={activity.player_has_responded || timedOut}
            onSubmit={async (data) => {
              setIsSubmitting(true)
              await guestPlayerService.submitResponse(runCode, activity.id, data)
              setIsSubmitting(false)
            }}
            onComplete={() => completeAndAdvance(activity.id)}
          />
        )

      default:
        return (
          <div className={styles.stack4}>
            <h3 className={styles.heading}>{activity.activity_data?.question || activity.title}</h3>
            {activity.description && <p className={styles.description}>{activity.description}</p>}
            <p className={styles.stateDesc}>Activity type: {actType}</p>
            <button
              onClick={() => handleCompleteActivity(activity.id)}
              disabled={actionLoading}
              className={styles.btnComplete}
            >
              {actionLoading ? 'Completing...' : 'Mark as Complete'}
            </button>
          </div>
        )
    }
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        <Loader2 className={styles.spinner} />
      </div>
    )
  }

  const completedCount = activities.filter(a => a.progress_status === 'completed').length
  const allCompleted = completedCount === activities.length && activities.length > 0

  return (
    <div className={styles.container}>
      {/* Sidebar overlay backdrop (mobile only) */}
      {sidebarOpen && (
        <div className={styles.sidebarBackdrop} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarHeader}>
          <h2 className={styles.sidebarTitle}>{lessonName}</h2>
          <p className={styles.sidebarSubtitle}>
            {completedCount} of {activities.length} completed
          </p>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress?.completion_percentage || 0}%` }}
            />
          </div>
        </div>

        <div className={styles.activityList}>
          {activities.map((activity) => {
            const isSelected = activity.id === selectedActivityId
            const statusIcon = activity.progress_status === 'completed' ? (
              <CheckCircle className={`w-4 h-4 ${styles.iconComplete}`} />
            ) : activity.progress_status === 'in_progress' || activity.progress_status === 'unlocked' ? (
              <Unlock className={`w-4 h-4 ${styles.iconUnlocked}`} />
            ) : (
              <Lock className={`w-4 h-4 ${styles.iconLocked}`} />
            )

            return (
              <button
                key={activity.id}
                onClick={() => {
                  setSelectedActivityId(activity.id)
                  setSidebarOpen(false)
                }}
                className={`${styles.activityItem} ${isSelected ? styles.activityItemSelected : ''} ${activity.progress_status === 'locked' ? styles.activityItemLocked : ''}`}
              >
                {statusIcon}
                <div className="flex-1 min-w-0">
                  <p className={styles.activityTitle}>{activity.activity_data?.question || activity.title}</p>
                  <p className={styles.activityType}>
                    {activity.activity_type.replace(/_/g, ' ')}
                  </p>
                </div>
                {isSelected && <ChevronRight className={`w-4 h-4 ${styles.iconChevron}`} />}
              </button>
            )
          })}
        </div>

        <div className={styles.playerFooter}>
          <p className={styles.playerLabel}>Playing as</p>
          <p className={styles.playerName}>{playerName}</p>
        </div>
      </div>

      {/* Main content */}
      <div className={styles.main}>
        <div className={styles.topBar}>
          {/* Hamburger — lives in navbar on mobile */}
          <button
            className={styles.navHamburger}
            onClick={() => setSidebarOpen(o => !o)}
            aria-label="Toggle activity list"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <div className={styles.topBarLeft}>
            {selectedActivity && (
              <>
                <span className={styles.activityNumber}>Activity {selectedActivity.sequence_number} of {activities.length}</span>
                {/* Timer — desktop: left side; hidden on mobile via CSS */}
                {selectedActivity.progress_status === 'in_progress' && timeRemaining !== null && (
                  <span className={`${styles.timer} ${styles.timerDesktopOnly} ${timeRemaining <= 60 ? styles.timerDanger : ''}`}>
                    <Clock className="w-4 h-4" />
                    {formatTime(timeRemaining)}
                  </span>
                )}
              </>
            )}
          </div>

          <div className={styles.topBarRight}>
            {/* Timer — mobile only, replaces completion % */}
            {selectedActivity?.progress_status === 'in_progress' && timeRemaining !== null && (
              <span className={`${styles.timer} ${styles.timerMobileOnly} ${timeRemaining <= 60 ? styles.timerDanger : ''}`}>
                <Clock className="w-4 h-4" />
                {formatTime(timeRemaining)}
              </span>
            )}
            {/* Completion % — hidden on mobile (visible in hamburger sidebar) */}
            <span className={styles.completionPct}>
              {Math.round(progress?.completion_percentage || 0)}% complete
            </span>
          </div>
        </div>

        <div className={styles.contentArea}>
          {error && (
            <div className={styles.errorBanner}>
              <p className={styles.errorText}>{error}</p>
              <button onClick={() => setError(null)} className={styles.errorDismiss}>Dismiss</button>
            </div>
          )}

          {allCompleted ? (
            <div className={styles.reportCard}>
              <div className={styles.reportHeader}>
                <Trophy className={styles.reportHeaderIcon} />
                <h2 className={styles.reportHeaderTitle}>All Activities Completed!</h2>
                <p className={styles.reportHeaderSubtitle}>Great work, {playerName}!</p>
              </div>

              {progress?.overall_score != null && (
                <div className={styles.reportOverall}>
                  <p className={styles.reportOverallLabel}>Overall Score</p>
                  <p className={styles.reportOverallScore}>{Math.min(100, Math.round(progress.overall_score))}%</p>
                  <div className={styles.reportOverallBar}>
                    <div
                      className={styles.reportOverallBarFill}
                      style={{ width: `${Math.min(100, progress.overall_score)}%` }}
                    />
                  </div>
                  <p className={styles.reportOverallCount}>
                    {progress.total_scored_activities} scored {progress.total_scored_activities === 1 ? 'activity' : 'activities'}
                  </p>
                </div>
              )}

              <div className={styles.reportSection}>
                <h3 className={styles.reportSectionTitle}>Your Results</h3>
                <div className={styles.reportList}>
                  {activities.map((activity) => {
                    const summary = activity.response_data_summary
                    const isViewed = summary?.viewed
                    const score = activity.score
                    const hasScore = score != null && !isViewed

                    const scoreClass = hasScore
                      ? score >= 70 ? styles.reportScoreHigh
                        : score >= 40 ? styles.reportScoreMid
                        : styles.reportScoreLow
                      : ''

                    return (
                      <div key={activity.id} className={styles.reportRow}>
                        <div className={styles.reportRowLeft}>
                          {isViewed ? (
                            <Eye className={`w-4 h-4 ${styles.reportIconViewed}`} />
                          ) : hasScore && score >= 70 ? (
                            <CheckCircle className={`w-4 h-4 ${styles.reportIconPass}`} />
                          ) : hasScore ? (
                            <XCircle className={`w-4 h-4 ${styles.reportIconFail}`} />
                          ) : (
                            <CheckCircle className={`w-4 h-4 ${styles.reportIconPass}`} />
                          )}
                          <div className={styles.reportRowInfo}>
                            <p className={styles.reportRowTitle}>
                              {activity.sequence_number}. {activity.title}
                            </p>
                            <p className={styles.reportRowType}>
                              {activity.activity_type.replace(/_/g, ' ')}
                            </p>
                          </div>
                        </div>
                        <div className={styles.reportRowRight}>
                          {isViewed ? (
                            <span className={styles.reportViewedBadge}>Viewed</span>
                          ) : hasScore ? (
                            <span className={`${styles.reportScoreBadge} ${scoreClass}`}>
                              {Math.min(100, Math.round(score))}%
                            </span>
                          ) : (
                            <span className={styles.reportViewedBadge}>Done</span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          ) : selectedActivity ? (
            <div className={styles.card}>
              {renderActivityContent(selectedActivity)}
            </div>
          ) : (
            <div className={styles.noSelection}>
              <BookOpen className={styles.noSelectionIcon} />
              <p className={styles.noSelectionText}>Select an activity from the sidebar to begin.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
